import { Button } from "@babackapp/customer-ui/components/button";
import {
  Option,
  OptionAction,
  OptionDescription,
  OptionsList,
  OptionTitle,
} from "@babackapp/customer-ui/components/option";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckIcon, PackageIcon, StoreIcon, UndoIcon } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { StepHeader } from "../components/step-header";
import { useSetReturnMode } from "../hooks/use-set-return-mode";
import { useWidgetParams } from "../hooks/use-widget-params";
import type { ReturnMode } from "../types";
import { useCustomText } from "../hooks/use-custom-text";

const AVAILABLE_RETURN_MODES: ReturnMode[] = ["carrier", "store", "retractation"];

const returnMethodSchema = z.object({
  mode: z.enum(["carrier", "store", "retractation"] as const),
});

type ReturnMethodFormValues = z.infer<typeof returnMethodSchema>;

export function ReturnMethodPage() {
  const { orderId } = useWidgetParams();
  const navigate = useNavigate();
  const setReturnMode = useSetReturnMode(orderId);
  const t = useCustomText();

  const METHOD_CONFIG: Array<{
    mode: ReturnMode;
    label: string;
    description: string;
    icon: React.ReactNode;
  }> = [
    {
      mode: "carrier",
      label: t("returnMethod.carrier.label"),
      description: t("returnMethod.carrier.description"),
      icon: <PackageIcon />,
    },
    {
      mode: "store",
      label: t("returnMethod.store.label"),
      description: t("returnMethod.store.description"),
      icon: <StoreIcon />,
    },
    {
      mode: "retractation",
      label: t("returnMethod.retractation.label"),
      description: t("returnMethod.retractation.description"),
      icon: <UndoIcon />,
    },
  ];

  const form = useForm<ReturnMethodFormValues>({
    resolver: zodResolver(returnMethodSchema),
  });

  const modes = METHOD_CONFIG.filter((m) => AVAILABLE_RETURN_MODES.includes(m.mode));
  const selected = form.watch("mode");

  const onSubmit = form.handleSubmit((values) => {
    setReturnMode(values.mode);
    navigate(values.mode === "store" ? "../store-selection" : "../address");
  });

  return (
    <WidgetPage>
      <StepHeader
        title={t("returnMethod.title")}
        description={t("returnMethod.description")}
      />
      <form onSubmit={onSubmit} className="flex flex-1 flex-col gap-4">
        <div className="flex flex-1 flex-col overflow-y-auto">
          <Controller
            control={form.control}
            name="mode"
            render={({ field }) => (
              <OptionsList>
                {modes.map(({ mode, label, description, icon }) => (
                  <Option
                    key={mode}
                    data-state={field.value === mode ? "checked" : "unchecked"}
                    onClick={() => field.onChange(mode)}
                  >
                    <OptionTitle>
                      {icon}
                      {label}
                    </OptionTitle>
                    <OptionDescription>{description}</OptionDescription>
                    <OptionAction>
                      {field.value === mode && <CheckIcon className="text-primary" />}
                    </OptionAction>
                  </Option>
                ))}
              </OptionsList>
            )}
          />
        </div>
        <div className="mt-auto flex gap-2 pb-4 shrink-0">
          <Button type="button" variant="outline" size="lg" onClick={() => navigate(-1)}>
            {t("common.back")}
          </Button>
          <Button type="submit" size="lg" className="flex-1" disabled={!selected}>
            {t("common.continue")}
          </Button>
        </div>
      </form>
    </WidgetPage>
  );
}
